import { useEffect, useRef, useState } from "react";
import { getRun, listRuns, type BobRun, type BobRunReport } from "../../api/bobReports";
import { fmtWhen, runActivityText, runState, usd } from "../../lib/bobReports";
import type { ReportNav } from "./reportNav";
import RunReportView from "./RunReportView";
import SeedReportView from "./SeedReportView";
import { Failed, Loading } from "./reportParts";
import "./bobTheme.css";
import "./bobReports.css";

const PAGE = 50;
type KindFilter = "all" | "push" | "seed";
const KIND_FILTERS: ReadonlyArray<{ id: KindFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "push", label: "Pushes" },
  { id: "seed", label: "Whole pack" },
];
const KIND_LABEL: Readonly<Record<string, string>> = { push: "Push", seed: "Whole pack", manual: "Manual" };
const STATE_LABEL: Readonly<Record<string, string>> = {
  completed: "Completed", partial: "Partial", failed: "Did not finish", displaced: "Superseded",
};

function RunsList({ nav }: { nav: ReportNav }) {
  const [kind, setKind] = useState<KindFilter>("all");
  const [runs, setRuns] = useState<BobRun[] | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [retry, setRetry] = useState(0);
  const gen = useRef(0);

  useEffect(() => {
    const mine = ++gen.current;
    setRuns(null);
    setError(null);
    listRuns(nav.project, { kind: kind === "all" ? null : kind, limit: PAGE }).then(
      (r) => {
        if (gen.current !== mine) return;
        setRuns(r.items);
        setTotal(r.total);
        setHasMore(r.has_more);
      },
      (e: Error) => { if (gen.current === mine) setError(e.message); },
    );
  }, [nav.project, kind, retry]);

  const loadMore = () => {
    if (!runs) return;
    const mine = gen.current;
    setLoadingMore(true);
    listRuns(nav.project, { kind: kind === "all" ? null : kind, limit: PAGE, offset: runs.length }).then(
      (r) => {
        if (gen.current !== mine) return;
        const seen = new Set(runs.map((x) => x.id));
        setRuns([...runs, ...r.items.filter((x) => !seen.has(x.id))]);
        setTotal(r.total);
        setHasMore(r.has_more && r.items.length > 0);
        setLoadingMore(false);
      },
      (e: Error) => { if (gen.current === mine) { setError(e.message); setLoadingMore(false); } },
    );
  };

  return (
    <div className="br-page br-narrow">
      <header className="br-header">
        <div className="br-header-text">
          <span className="br-eyebrow">Bob code review</span>
          <h2 className="br-h1">Reports</h2>
          <span className="br-soft">Every review Bob ran on {nav.projectName}, newest first.{runs ? ` ${total} in all.` : ""}</span>
        </div>
        <div className="br-seg" role="group" aria-label="Kind of review">
          {KIND_FILTERS.map((k) => (
            <button key={k.id} type="button" aria-pressed={kind === k.id} onClick={() => setKind(k.id)}>{k.label}</button>
          ))}
        </div>
      </header>
      {error && !runs ? <Failed message={`The reviews could not be loaded: ${error}`} onRetry={() => setRetry((n) => n + 1)} />
        : !runs ? <Loading what="reviews" />
        : runs.length === 0 ? <div className="br-state">No reviews yet.</div>
        : (
          <section className="br-panel" aria-label="Reviews">
            <div className="br-table-wrap">
              <table className="br-table" style={{ minWidth: 640 }}>
                <thead>
                  <tr>
                    <th scope="col">Run</th>
                    <th scope="col">When</th>
                    <th scope="col">Kind</th>
                    <th scope="col">What happened</th>
                    <th scope="col" className="br-r">New</th>
                    <th scope="col" className="br-r">Fixed</th>
                    {nav.isAdmin && <th scope="col" className="br-r">Cost</th>}
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="br-clickable" data-run={r.id} onClick={() => nav.openRun(r.id)}>
                      <td className="br-num">
                        <a href={nav.runHref(r.id)} onClick={(e) => { e.preventDefault(); e.stopPropagation(); nav.openRun(r.id); }}>#{r.id}</a>
                      </td>
                      <td className="br-mono br-muted br-tiny" style={{ whiteSpace: "nowrap" }}>{fmtWhen(r.created_at)}</td>
                      <td>{KIND_LABEL[r.kind] ?? r.kind}</td>
                      <td className="br-soft">
                        {runActivityText(r)}
                        {runState(r) !== "completed" && <> <span className="br-chip br-chip-cat">{STATE_LABEL[runState(r)]}</span></>}
                      </td>
                      <td className="br-r br-num">{r.new_count}</td>
                      <td className="br-r br-num">{r.fixed_count}</td>
                      {nav.isAdmin && <td className="br-r br-num br-muted">{typeof r.cost_usd === "number" ? usd(r.cost_usd) : ""}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {error && <div className="br-note" role="alert">{error}</div>}
            {hasMore && (
              <button type="button" className="br-btn" style={{ alignSelf: "flex-start" }} disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            )}
          </section>
        )}
    </div>
  );
}

function RunReport({ id, nav }: { id: number; nav: ReportNav }) {
  const [report, setReport] = useState<BobRunReport | null>(null);
  const [error, setError] = useState<{ message: string; status?: number } | null>(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let live = true;
    setReport(null);
    setError(null);
    getRun(nav.project, id).then(
      (r) => { if (live) setReport(r); },
      (e: Error & { status?: number }) => { if (live) setError({ message: e.message, status: e.status }); },
    );
    return () => { live = false; };
  }, [nav.project, id, retry]);

  if (error) {
    return (
      <div className="br-page br-narrow">
        {error.status === 404
          ? <div className="br-state" role="alert">Run #{id} is not one of {nav.projectName}'s reviews.</div>
          : <Failed message={`Run #${id} could not be loaded: ${error.message}`} onRetry={() => setRetry((n) => n + 1)} />}
        <button type="button" className="br-btn" style={{ alignSelf: "center" }} onClick={() => nav.openRun(null)}>All reviews</button>
      </div>
    );
  }
  if (!report) return <div className="br-page br-narrow"><Loading what={`run #${id}`} /></div>;
  return report.run.kind === "seed" ? <SeedReportView report={report} nav={nav} /> : <RunReportView report={report} nav={nav} />;
}

/** The Reports tab: the runs list, or the run `?run=` names. */
export default function BobReports({ nav }: { nav: ReportNav }) {
  return (
    <div className="bob br-root">
      {nav.runId === null ? <RunsList nav={nav} /> : <RunReport id={nav.runId} nav={nav} />}
    </div>
  );
}
